import clsx from "clsx"
import { FC, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { SpinnerCircular } from "spinners-react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Swiper as SwiperType } from "swiper/types"
import { ArrowLeftIcon } from "@/assets"
import { useModal } from "@/modals"
import { useGetMatchBidVariantsQuery } from "@/store/apis/matchAPI"
import { Button } from "@/ui/Button"
import { LabrBalancePill } from "@/ui/LabrBalancePill/LabrBalancePill.tsx"
import { Modal } from "@/ui/Modal"
import { OutlinedText } from "@/ui/OutlinedText"
import styles from "./TonRoomsModal.module.scss"
import { TonRoom } from "./ui/TonRoom/TonRoom.tsx"

const modalType = "tonRooms"

const PER_GROUP = 3

export const TonRoomsModal: FC = () => {
  const { t } = useTranslation()
  const { modal, openModal } = useModal(modalType)

  const [activeSlide, setActiveSlide] = useState<number>(0)

  const activeGroup = Math.floor(activeSlide / PER_GROUP)

  const refSwiper = useRef<SwiperType | null>(null)

  const { data: bidVariantsData, isLoading: isBidVariantsLoading } =
    useGetMatchBidVariantsQuery({
      page: 1,
      limit: 6
    })

  const list = bidVariantsData?.items || []

  const handleClose = () => {
    modal.close()
  }

  const handleInfo = () => {
    openModal("tonMatchInfo")
  }

  return (
    <Modal.Center
      type={modalType}
      className={clsx(styles.TonRoomsModal)}
      body={{ className: styles.TonRoomsModal_body }}
    >
      <Modal.StoneFlexContainer
        className={styles.TonRoomsModal_container}
        styles={"ton"}
        content={{ className: styles.TonRoomsModal_content }}
        outside={
          <div className={clsx(styles.TonRoomsModal_footerButtons)}>
            <Button.Ring
              className={clsx(styles.HowItWorksModal_button, styles.prev)}
              disabled={activeSlide === 0}
              onClick={() => {
                refSwiper.current?.slidePrev()
              }}
            >
              <ArrowLeftIcon />
            </Button.Ring>
            <Button
              className={clsx(styles.TonRoomsModal_footerButtons_item)}
              styles="green"
              onClick={handleClose}
            >
              {t("button.close")}
            </Button>
            <Button.Ring
              className={clsx(styles.HowItWorksModal_button, styles.next)}
              disabled={activeSlide >= list.length - PER_GROUP}
              onClick={() => {
                refSwiper.current?.slideNext()
              }}
            >
              <ArrowLeftIcon />
            </Button.Ring>
          </div>
        }
      >
        <div className={clsx(styles.TonRoomsModal_header)}>
          <div className={clsx(styles.TonRoomsModal_header_inner)}>
            <LabrBalancePill
              className={clsx(styles.TonRoomsModal_balance)}
              currency="ton"
            />
            <OutlinedText className={clsx(styles.TonRoomsModal_title)}>
              {t("playForTon.title")}
            </OutlinedText>
            <OutlinedText className={clsx(styles.TonRoomsModal_text)}>
              {t("playForTon.selectLobby")}
            </OutlinedText>
            <Button.Info
              className={clsx(styles.TonRoomsModal_infoButton)}
              onClick={handleInfo}
            />
          </div>
        </div>
        <div className={clsx(styles.TonRoomsModal_swiperContainer)}>
          {isBidVariantsLoading && (
            <SpinnerCircular size={32} color="#35262699" />
          )}
          {!isBidVariantsLoading && (
            <Swiper
              className={clsx(styles.TonRoomsModal_swiper)}
              autoHeight={true}
              slidesPerView={PER_GROUP}
              slidesPerGroup={PER_GROUP}
              spaceBetween={8}
              grabCursor={true}
              allowTouchMove={true}
              onSwiper={(swiper) => {
                refSwiper.current = swiper
              }}
              onSlideChange={(e) => {
                setActiveSlide(e.activeIndex)
              }}
            >
              {list.map((bidVariant) => (
                <SwiperSlide key={bidVariant._id}>
                  <TonRoom {...bidVariant} />
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
        <div className={clsx(styles.TonRoomsModal_indicator)}>
          {Array.from({
            length: Math.ceil(list.length / PER_GROUP)
          }).map((_, index) => (
            <div
              key={index}
              className={clsx(styles.TonRoomsModal_indicator_dot, {
                [styles.active]: index === activeGroup
              })}
            />
          ))}
        </div>
      </Modal.StoneFlexContainer>
    </Modal.Center>
  )
}
